import { useState, useCallback } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Hook to manage Global Climate Atlas data for farm location selection
 * Fetches the nearest 7 hexagons (center + ring) for microclimate selection
 */
export const useGlobalClimate = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch the 7 nearest hexagons to a specific point (center + surrounding ring)
   * Populates climateHexes array and returns the closest hex for auto-selection
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} count - Number of hexes to fetch (default 7)
   * @returns {Promise<Object>} The closest hex (for auto-selection)
   */
  const fetchNearest = useCallback(async (lat, lng, count = 7) => {
    if (!lat || !lng) return null

    setLoading(true)
    setError(null)

    const { data: hexes, error: fetchError } = await supabaseGetter.globalClimateAtlas.getNearestMultiple(
      lat, lng, count
    )

    if (fetchError) {
      setError(fetchError.message)
      setLoading(false)
      return null
    }

    // Populate the hexes array for map display
    setData(hexes || [])
    setLoading(false)

    // Return the first (closest) hex for auto-selection
    return hexes && hexes.length > 0 ? hexes[0] : null
  }, [])

  /**
   * Fetch interpolated climate data from N nearest hexes using IDW
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} count - Number of hexes to interpolate (default 7)
   * @returns {Promise<Object>} Interpolated climate data with contributing hex metadata
   */
  const fetchInterpolated = useCallback(async (lat, lng, count = 7) => {
    if (!lat || !lng) return null

    setLoading(true)
    setError(null)

    const { data: interpolated, error: fetchError } =
      await supabaseGetter.globalClimateAtlas.getInterpolated(lat, lng, count)

    if (fetchError) {
      setError(fetchError.message)
      setLoading(false)
      return null
    }

    setLoading(false)
    return interpolated
  }, [])

  return {
    climateHexes: data,
    loading,
    error,
    fetchNearest,
    fetchInterpolated
  }
}
